import { DollarOutlined } from "@ant-design/icons";
import { useQuery } from "@tanstack/react-query";
import React from "react";
import { Alert, Button, Flex, Segmented, StatusText, Typography } from "../../../../../../../shared/frontend/ui";
import type { ApiClient } from "../../../app/api/client";
import type { I18nContextValue } from "../../../app/i18n/useI18n";
import { formatDateOnly, formatMoney } from "../../../app/units/format";
import type { UnitsSettings } from "../../../app/units/units";
import { paymentMethodLabel } from "../../../app/utils/paymentMethod";
import type { ColumnDefinition } from "../../../app/utils/useProTableColumnEditor";
import { LoadingOverlay } from "../../../components/LoadingOverlay";
import { SettingsListPage, type ProColumnsWithMeasure } from "../../../components/SettingsListPage";
import { personDetailQueryKeys } from "./personDetailQueryKeys";
import { ISO_DATE_LENGTH } from "../../../app/utils/isoDate";

const REVERSAL_REASON_CHARGEBACK = "Chargeback";
const REVERSAL_REASON_REFUND = "Refund";
const PAYMENT_REASON_CREDIT_OFFSET = "creditOffset";
const LINE_STATUS_PAID = "paid";

/** Antwort von GET /api/v1/open-items/statement (PersonAccountStatementService.Statement). */
export type AccountStatementLine = {
  id: string;
  date: string;
  kind: "claim" | "cancellation" | "credit" | "payment" | "paymentReversal" | "unassignedPayment" | "adjustment" | "installmentPlan";
  description: string;
  debit?: number | null;
  credit?: number | null;
  balance?: number | null;
  isFuture: boolean;
  affectsBalance: boolean;
  openItemId?: string | null;
  paymentId?: string | null;
  groupId: string;
  status?: string | null;
  openAmount?: number | null;
  paymentMethod?: number | null;
  reason?: string | null;
  periodDescription?: string | null;
  installments?: { count: number; openCount: number; amount: number; total: number } | null;
};

export type AccountStatement = {
  balance: number;
  dueAmount: number;
  inCollectionAmount: number;
  debtCollectionAmount: number;
  futureAmount: number;
  oldestDueDate?: string | null;
  dueSinceDays?: number | null;
  reminderDaysAfterDueDate?: number | null;
  reminderEligibleFrom?: string | null;
  reminderEligibleAmount: number;
  nextCollection?: { date: string; amount: number; isDirectDebit: boolean } | null;
  lines: AccountStatementLine[];
};

type StatementRow = AccountStatementLine & { seq: number };
type TextFn = I18nContextValue["text"];

const MONEY_TOLERANCE = 0.005;

const STATEMENT_COLUMN_DEFINITIONS: ColumnDefinition[] = [
  { key: "date", label: "Date", defaultVisible: true },
  { key: "description", label: "Account statement booking", defaultVisible: true },
  { key: "status", label: "Account statement path", defaultVisible: true },
  { key: "debit", label: "Account statement debit", defaultVisible: true },
  { key: "credit", label: "Account statement credit", defaultVisible: true },
  { key: "balance", label: "Account statement balance", defaultVisible: true },
];

const STATUS_LABEL: Record<string, { key: string; tone?: string }> = {
  planned: { key: "Account statement status planned" },
  due: { key: "Account statement status due", tone: "warning" },
  reminderEligible: { key: "Account statement status reminder eligible", tone: "error" },
  inCollection: { key: "Account statement status in collection", tone: "processing" },
  paid: { key: "Account statement status paid", tone: "success" },
  settled: { key: "Account statement status settled" },
  cancelled: { key: "Account statement status cancelled" },
  debtCollection: { key: "Account statement status debt collection", tone: "error" },
};

const ADJUSTMENT_LABEL: Record<string, string> = {
  creditApplied: "Account statement adjustment credit applied",
  cancelledWithoutCredit: "Account statement adjustment cancelled",
  cancelledAfterPayment: "Account statement adjustment cancelled after payment",
  overpayment: "Account statement adjustment overpayment",
  creditOverapplied: "Account statement adjustment credit overapplied",
  other: "Account statement adjustment other",
};

function isOpenLine(line: AccountStatementLine): boolean {
  return (line.kind === "claim" || line.kind === "credit" || line.kind === "cancellation")
    && Math.abs(Number(line.openAmount ?? 0)) > MONEY_TOLERANCE;
}

function lineDescription(line: AccountStatementLine, text: TextFn, units: UnitsSettings): string {
  switch (line.kind) {
    case "adjustment":
      return text(ADJUSTMENT_LABEL[line.reason ?? "other"] ?? "Account statement adjustment other");
    case "installmentPlan": {
      const plan = line.installments;
      const detail = plan
        ? text("Account statement installment plan detail")
          .replace("{0}", String(plan.count))
          .replace("{1}", formatMoney(plan.amount, units))
          .replace("{2}", String(plan.openCount))
        : "";
      return [text("Account statement installment plan"), line.description, detail].filter(Boolean).join(" · ");
    }
    case "paymentReversal":
      return line.reason === REVERSAL_REASON_CHARGEBACK ? text("Payment chargeback line")
        : line.reason === REVERSAL_REASON_REFUND ? text("Payment refund") : text("Payment reversal line");
    case "payment":
    case "unassignedPayment": {
      const isCreditOffset = line.reason === PAYMENT_REASON_CREDIT_OFFSET;
      const method = isCreditOffset ? text("Account statement credit offset") : paymentMethodLabel(line.paymentMethod);
      const base = [method, isCreditOffset ? "" : line.description.trim()].filter(Boolean).join(" · ");
      return line.kind === "unassignedPayment" ? `${base} · ${text("Account statement unassigned payment")}` : base;
    }
    default:
      return line.description || text("Open item");
  }
}

function renderStatus(line: AccountStatementLine, text: TextFn, units: UnitsSettings): React.ReactNode {
  if (!line.status || line.kind === "installmentPlan") return null;
  const label = STATUS_LABEL[line.status];
  if (!label) return null;
  const open = Math.abs(Number(line.openAmount ?? 0));
  const full = Number(line.debit ?? line.credit ?? 0);
  // Teilweise beglichene Forderung: Rest neben dem Weg, damit Soll und Rest nicht verwechselt werden.
  const rest = line.status !== LINE_STATUS_PAID && open > MONEY_TOLERANCE && Math.abs(open - full) > MONEY_TOLERANCE
    ? ` · ${text("Account statement rest").replace("{0}", formatMoney(open, units))}` : "";
  return <StatusText color={label.tone}>{text(label.key)}{rest}</StatusText>;
}

function money(value: number | null | undefined, units: UnitsSettings): React.ReactNode {
  return value == null ? null : formatMoney(value, units);
}

type Props = {
  api: Pick<ApiClient, "get">;
  text: TextFn;
  units: UnitsSettings;
  personId: string;
  studioIdsParam?: string;
  canWriteFinance: boolean;
  onRecordPayment: () => void;
};

/**
 * Kontoauszug der Akte (Tafel A1): Buchungen chronologisch mit laufendem Saldo, neueste oben.
 * Der Saldo entspricht der Summe der offenen Posten; weichen Einzelbuchungen davon ab, steht eine benannte
 * Ausgleichszeile im Auszug (Regel im Backend, PersonAccountStatementService).
 */
export function PersonAccountStatementTab({ api, text, units, personId, studioIdsParam, canWriteFinance, onRecordPayment }: Props) {
  const [onlyOpen, setOnlyOpen] = React.useState(false);
  const statementQuery = useQuery({
    // Unter dem Schlüssel der offenen Posten, damit jede Invalidierung nach Zahlung, Storno oder Lauf ihn mitnimmt.
    queryKey: [...personDetailQueryKeys.person.openItems(personId), "statement", studioIdsParam ?? ""],
    queryFn: () => {
      const q = new URLSearchParams({ personId });
      if (studioIdsParam) q.set("studioIds", studioIdsParam);
      return api.get<AccountStatement>(`/api/v1/open-items/statement?${q.toString()}`);
    },
    enabled: !!personId,
    retry: false,
  });
  const statement = statementQuery.data;

  // Künftige Beiträge gehören nicht in den Kontoauszug: er zeigt, was gebucht und fällig ist.
  const rows = React.useMemo<StatementRow[]>(() => (statement?.lines ?? [])
    .map((line, seq) => ({ ...line, seq }))
    .filter((line) => !line.isFuture && (!onlyOpen || isOpenLine(line)))
    .reverse(), [onlyOpen, statement]);

  const columns = React.useMemo<Record<string, ProColumnsWithMeasure<StatementRow>>>(() => ({
    date: {
      title: text("Date"),
      dataIndex: "date",
      key: "date",
      ellipsis: false,
      defaultSortOrder: "descend",
      // Buchungsreihenfolge statt Datum allein: am selben Tag bleibt Forderung vor Zahlung vor Ausgleich.
      sortValue: (row) => row.seq,
      render: (_, row) => formatDateOnly(String(row.date).slice(0, ISO_DATE_LENGTH), units),
    },
    description: {
      title: text("Account statement booking"),
      key: "description",
      ellipsis: false,
      sortValue: (row) => lineDescription(row, text, units),
      render: (_, row) => (
        <Flex vertical gap={0}>
          <Typography.Text type={row.affectsBalance ? undefined : "secondary"}>{lineDescription(row, text, units)}</Typography.Text>
          {row.periodDescription ? <Typography.Text type="secondary">{row.periodDescription}</Typography.Text> : null}
        </Flex>
      ),
    },
    status: {
      title: text("Account statement path"),
      dataIndex: "status",
      key: "status",
      ellipsis: false,
      sortValue: (row) => row.status ?? "",
      render: (_, row) => renderStatus(row, text, units),
    },
    debit: {
      title: text("Account statement debit"),
      dataIndex: "debit",
      key: "debit",
      align: "right",
      ellipsis: false,
      sortValue: (row) => Number(row.debit ?? 0),
      render: (_, row) => money(row.debit, units),
    },
    credit: {
      title: text("Account statement credit"),
      dataIndex: "credit",
      key: "credit",
      align: "right",
      ellipsis: false,
      sortValue: (row) => Number(row.credit ?? 0),
      render: (_, row) => money(row.credit, units),
    },
    balance: {
      title: text("Account statement balance"),
      dataIndex: "balance",
      key: "balance",
      align: "right",
      ellipsis: false,
      sortValue: (row) => Number(row.balance ?? 0),
      render: (_, row) => money(row.balance, units),
    },
  }), [text, units]);

  const requestRows = React.useCallback(async () => ({ data: rows, success: !statementQuery.isError, total: rows.length }), [rows, statementQuery.isError]);

  const balance = Number(statement?.balance ?? 0);
  const balanceLabel = balance < -MONEY_TOLERANCE
    ? text("Account statement credit balance").replace("{0}", formatMoney(-balance, units))
    : balance > MONEY_TOLERANCE
      ? text("Account statement open balance").replace("{0}", formatMoney(balance, units))
      : formatMoney(0, units);
  const balanceNote = [
    statement && statement.inCollectionAmount > MONEY_TOLERANCE
      ? text("Account statement in collection amount").replace("{0}", formatMoney(statement.inCollectionAmount, units)) : null,
    statement && statement.debtCollectionAmount > MONEY_TOLERANCE
      ? text("Account statement debt collection amount").replace("{0}", formatMoney(statement.debtCollectionAmount, units)) : null,
  ].filter(Boolean).join(" · ");
  return (
    <Flex vertical gap="middle" style={{ width: "100%", position: "relative" }}>
      <LoadingOverlay visible={statementQuery.isPending} scope="block" />
      {statementQuery.isError ? <Alert type="error" title={text("Account statement load failed")} /> : null}
      <SettingsListPage<StatementRow>
        storageKey="persons.detail.statement"
        rowKey="id"
        request={requestRows}
        columnDefinitions={STATEMENT_COLUMN_DEFINITIONS}
        allColumnsMap={columns}
        pagination={{ pageSize: 50 }}
        toolbarExtra={(
          <Segmented
            value={onlyOpen ? "open" : "all"}
            onChange={(value) => setOnlyOpen(value === "open")}
            options={[
              { value: "all", label: text("Account statement all bookings") },
              { value: "open", label: text("Account statement only open") },
            ]}
          />
        )}
        toolbarRightExtra={canWriteFinance ? (
          <Button type="primary" icon={<DollarOutlined />} onClick={onRecordPayment}>
            {text("Account statement payment")}
          </Button>
        ) : undefined}
      />
      <Typography.Text>
        <Typography.Text strong>{text("Account statement balance")}: {balanceLabel}</Typography.Text>
        {balanceNote ? <Typography.Text type="secondary"> · {balanceNote}</Typography.Text> : null}
      </Typography.Text>
    </Flex>
  );
}
